import { TextField } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  CancelOutlined,
  CheckCircleOutline,
  WarningAmber,
} from '@mui/icons-material';

import { useOrder } from '../../../../context/OrderContext';
import { Order, OrderCancellationReason } from '../../../../models/Order';
import Button from '../../../formElements/Button';
import Modal from '../../Modal';

type FormData = {
  cancellationReason: OrderCancellationReason;
  cancellationMessage: string;
};

type Props = {
  order: Order;
  isModalVisible: boolean;
  setIsModalVisible: (value: boolean) => void;
};

function CancelOrder({ order, isModalVisible, setIsModalVisible }: Props) {
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const { cancelOrder } = useOrder();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      cancellationReason: OrderCancellationReason.CUSTOMER_REQUEST,
      cancellationMessage: '',
    },
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  const handleSubmitForm = async (data: FormData) => {
    setIsModalVisible(false);
    const toastId = toast.loading('Anulowanie zamówienia...');
    try {
      await cancelOrder(
        order.id,
        data.cancellationReason,
        data.cancellationMessage,
      );
      toast.update(toastId, {
        render: 'Zamówienie zostało anulowane!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      reset();
    } catch (error: any) {
      toast.update(toastId, {
        render: error.message || 'Wystąpił błąd podczas anulowania zamówienia',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  return (
    <Modal
      show={isModalVisible}
      onClose={() => setIsModalVisible(false)}
      size="md"
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="sm:min-w-[400px]"
      >
        {/* Nagłówek */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <div className="bg-amber-50 rounded-full p-3">
            <WarningAmber fontSize="large" className="text-amber-500" />
          </div>
          <h2 className="text-xl font-medium text-gray-900">
            Anulowanie zamówienia
          </h2>
        </motion.div>

        {/* Informacje o zamówieniu */}
        <motion.div
          variants={itemVariants}
          className="bg-gray-50 rounded-lg p-4 mb-6"
        >
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm text-gray-500">Numer zamówienia:</span>
              <p className="font-medium text-gray-800">#{order.orderNumber}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Wartość:</span>
              <p className="font-medium text-gray-800">{order.totalPrice} zł</p>
            </div>
          </div>
        </motion.div>

        {/* Formularz */}
        <form id="cancelOrderForm" onSubmit={handleSubmit(handleSubmitForm)}>
          <motion.div variants={itemVariants} className="space-y-4 mb-6">
            {/* Powód anulowania */}
            <div>
              <label
                htmlFor="cancellationReason"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Powód anulowania
              </label>
              <Controller
                name="cancellationReason"
                control={control}
                rules={{ required: 'Musisz wybrać powód anulowania' }}
                render={({ field }) => (
                  <div className="relative">
                    <select
                      id="cancellationReason"
                      {...field}
                      className={`block w-full pl-3 pr-10 py-2.5 text-base focus:outline-none border rounded-md appearance-none
                        ${
                          errors.cancellationReason
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-primary focus:border-primary'
                        }
                        text-gray-700 bg-white shadow-sm sm:text-sm`}
                      onChange={(e) => {
                        field.onChange(e);
                        setIsOtherSelected(
                          e.target.value === OrderCancellationReason.OTHER,
                        );
                      }}
                    >
                      <option value={OrderCancellationReason.CUSTOMER_REQUEST}>
                        Na życzenie klienta
                      </option>
                      <option value={OrderCancellationReason.OUT_OF_STOCK}>
                        Brak towaru
                      </option>
                      <option value={OrderCancellationReason.OTHER}>
                        Inny powód
                      </option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              />
              {errors.cancellationReason && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.cancellationReason.message}
                </p>
              )}
            </div>

            {/* Szczegóły powodu */}
            {isOtherSelected && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Controller
                  name="cancellationMessage"
                  control={control}
                  rules={{
                    required: "Musisz podać szczegóły dla opcji 'Inny powód'",
                  }}
                  render={({ field }) => (
                    <TextField
                      label="Szczegóły powodu"
                      fullWidth
                      margin="normal"
                      multiline
                      rows={3}
                      {...field}
                      error={!!errors.cancellationMessage}
                      helperText={
                        errors.cancellationMessage
                          ? errors.cancellationMessage.message
                          : ''
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: errors.cancellationMessage
                              ? 'rgb(239, 68, 68)'
                              : 'rgb(209, 213, 219)',
                          },
                          '&:hover fieldset': {
                            borderColor: errors.cancellationMessage
                              ? 'rgb(239, 68, 68)'
                              : 'rgb(156, 163, 175)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: errors.cancellationMessage
                              ? 'rgb(239, 68, 68)'
                              : 'rgb(79, 70, 229)',
                          },
                        },
                      }}
                    />
                  )}
                />
              </motion.div>
            )}
          </motion.div>

          {/* Przyciski akcji */}
          <motion.div
            variants={itemVariants}
            className="flex sm:flex-row flex-col-reverse gap-3"
          >
            <motion.div
              className="flex-1"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={() => setIsModalVisible(false)}
                size="small"
                colorTheme="outline"
                className="w-full py-2.5 flex items-center justify-center gap-2"
                type="button"
              >
                <CancelOutlined fontSize="small" />
                <span>Zachowaj zamówienie</span>
              </Button>
            </motion.div>

            <motion.div
              className="flex-1"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={handleSubmit(handleSubmitForm)}
                size="small"
                className="w-full py-2.5 flex items-center justify-center gap-2"
                type="button"
              >
                <CheckCircleOutline fontSize="small" />
                <span>Anuluj zamówienie</span>
              </Button>
            </motion.div>
          </motion.div>
        </form>

        {/* Dodatkowe informacje */}
        <motion.div
          variants={itemVariants}
          className="mt-6 text-center text-xs text-gray-500"
        >
          Uwaga: Anulowanie zamówienia jest nieodwracalne
        </motion.div>
      </motion.div>
    </Modal>
  );
}

export default CancelOrder;
